import { useMemo, useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { format, addDays, isBefore, startOfDay, isSameDay } from "date-fns";
import { cn } from "@/lib/utils";
import { RiCalendarLine, RiTimeLine, RiCheckLine } from "@remixicon/react";

interface AvailabilitySchedule {
  day: string;
  available: boolean;
  startTime?: string;
  endTime?: string;
}

interface CustomDate {
  date: string | Date;
  available: boolean;
  startTime?: string;
  endTime?: string;
}

interface BookingDateTimePickerProps {
  availability?: {
    type: "always" | "scheduled" | "custom";
    schedule?: AvailabilitySchedule[];
    customDates?: CustomDate[];
  };
  duration?: string;
  timeInterval?: number;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  onDateChange: (date: Date | undefined) => void;
  onTimeChange: (startTime: string, endTime: string) => void;
  themeColors?: {
    primaryColor?: string;
    secondaryColor?: string;
  };
  className?: string;
}

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

const DEFAULT_DAY_START = "09:00";
const DEFAULT_DAY_END = "17:00";

function toLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseLocalDateKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function parseDurationMinutes(durationStr?: string): number {
  if (!durationStr) return 60;
  const lower = durationStr.toLowerCase();
  const num = parseInt(lower.match(/\d+/)?.[0] || "1", 10);
  if (lower.includes("day")) return num * 24 * 60;
  if (lower.includes("hour")) return num * 60;
  if (lower.includes("minute")) return num;
  return 60;
}

function minutesFromTime(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function timeFromMinutes(total: number): string {
  const wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(wrapped / 60);
  const mins = wrapped % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

function formatTimeLabel(time: string): string {
  try {
    return format(new Date(`2000-01-01T${time}`), "h:mm a");
  } catch {
    return time;
  }
}

function customDateKey(value: string | Date): string {
  if (typeof value === "string") {
    return value.includes("T") ? value.split("T")[0] : value.slice(0, 10);
  }
  return toLocalDateKey(new Date(value));
}

export function BookingDateTimePicker({
  availability,
  duration,
  timeInterval = 30,
  startDate,
  endDate,
  startTime,
  endTime,
  onDateChange,
  onTimeChange,
  themeColors = { primaryColor: "#3b82f6", secondaryColor: "#10b981" },
  className,
}: BookingDateTimePickerProps) {
  const primary = themeColors.primaryColor || "#3b82f6";
  const durationMinutes = parseDurationMinutes(duration);
  const isMultiDay = durationMinutes >= 24 * 60;
  const interval = Math.max(15, timeInterval || 30);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    startDate ? parseLocalDateKey(startDate) : undefined,
  );

  useEffect(() => {
    if (startDate) {
      const next = parseLocalDateKey(startDate);
      setSelectedDate((prev) =>
        prev && isSameDay(prev, next) ? prev : next,
      );
    } else {
      setSelectedDate(undefined);
    }
  }, [startDate]);

  const availabilityType = availability?.type || "always";

  const dayWindow = useMemo(() => {
    if (!selectedDate) {
      return { startTime: DEFAULT_DAY_START, endTime: DEFAULT_DAY_END };
    }

    const dayName = WEEKDAYS[selectedDate.getDay()];
    const dateKey = toLocalDateKey(selectedDate);

    if (availabilityType === "custom" && availability?.customDates?.length) {
      const match = availability.customDates.find(
        (d) => customDateKey(d.date) === dateKey && d.available,
      );
      if (match) {
        return {
          startTime: match.startTime || DEFAULT_DAY_START,
          endTime: match.endTime || DEFAULT_DAY_END,
        };
      }
    }

    if (availabilityType === "scheduled" && availability?.schedule?.length) {
      const match = availability.schedule.find(
        (s) => s.day === dayName && s.available,
      );
      if (match) {
        return {
          startTime: match.startTime || DEFAULT_DAY_START,
          endTime: match.endTime || DEFAULT_DAY_END,
        };
      }
    }

    return { startTime: DEFAULT_DAY_START, endTime: DEFAULT_DAY_END };
  }, [selectedDate, availability, availabilityType]);

  const isDateDisabled = (date: Date) => {
    const today = startOfDay(new Date());
    if (isBefore(date, today)) return true;

    if (availabilityType === "scheduled" && availability?.schedule?.length) {
      const dayName = WEEKDAYS[date.getDay()];
      return !availability.schedule.some(
        (s) => s.day === dayName && s.available,
      );
    }

    if (availabilityType === "custom" && availability?.customDates?.length) {
      const key = toLocalDateKey(date);
      return !availability.customDates.some(
        (d) => customDateKey(d.date) === key && d.available,
      );
    }

    return false;
  };

  const timeSlots = useMemo(() => {
    if (isMultiDay) return [];

    const startMin = minutesFromTime(dayWindow.startTime);
    const endMin = minutesFromTime(dayWindow.endTime);
    if (endMin <= startMin) return [];

    const slots: string[] = [];
    for (let minutes = startMin; minutes + durationMinutes <= endMin; minutes += interval) {
      slots.push(timeFromMinutes(minutes));
    }

    // If duration doesn't fit any slot window cleanly, still offer start times
    // that leave at least one interval before closing.
    if (slots.length === 0) {
      for (let minutes = startMin; minutes < endMin; minutes += interval) {
        slots.push(timeFromMinutes(minutes));
      }
    }

    return slots;
  }, [dayWindow, durationMinutes, interval, isMultiDay]);

  const visibleTimeSlots = useMemo(() => {
    if (!selectedDate) return timeSlots;
    const today = new Date();
    if (!isSameDay(selectedDate, today)) return timeSlots;

    const nowPlusBuffer = today.getHours() * 60 + today.getMinutes() + 15;
    return timeSlots.filter(
      (slot) => minutesFromTime(slot) >= nowPlusBuffer,
    );
  }, [timeSlots, selectedDate]);

  const availableWeekdays = useMemo(() => {
    if (availabilityType !== "scheduled" || !availability?.schedule) return [];
    return availability.schedule.filter((s) => s.available);
  }, [availability, availabilityType]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    onTimeChange("", "");
    onDateChange(date);
  };

  const handleTimeSelect = (slot: string) => {
    const startMin = minutesFromTime(slot);
    const windowEnd = minutesFromTime(dayWindow.endTime);
    const endMin = Math.min(startMin + durationMinutes, windowEnd);
    onTimeChange(slot, timeFromMinutes(endMin > startMin ? endMin : startMin + durationMinutes));
  };

  const selectionLabel = (() => {
    if (!startDate) return null;
    const dateLabel = format(parseLocalDateKey(startDate), "EEE, MMM d");
    if (isMultiDay && endDate) {
      return `${dateLabel} → ${format(parseLocalDateKey(endDate), "EEE, MMM d")}`;
    }
    if (startTime) {
      const endLabel = endTime ? ` – ${formatTimeLabel(endTime)}` : "";
      return `${dateLabel} · ${formatTimeLabel(startTime)}${endLabel}`;
    }
    return dateLabel;
  })();

  return (
    <div className={cn("space-y-4", className)}>
      <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
        {/* Calendar */}
        <div className="rounded-2xl border border-black/8 bg-white p-3">
          <div className="mb-2 flex items-center gap-2 px-1">
            <RiCalendarLine className="h-4 w-4" style={{ color: primary }} />
            <p className="text-sm font-semibold text-black/80">Pick a date</p>
          </div>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            disabled={isDateDisabled}
            initialFocus
            className="mx-auto w-full"
            classNames={{
              months: "flex flex-col w-full",
              month: "w-full space-y-3",
              caption: "flex justify-center pt-1 relative items-center",
              table: "w-full border-collapse",
              head_row: "flex w-full",
              head_cell:
                "text-muted-foreground rounded-md flex-1 font-normal text-[0.75rem]",
              row: "flex w-full mt-1",
              cell: "relative flex-1 p-0 text-center text-sm focus-within:relative focus-within:z-20",
              day: cn(
                "h-9 w-full rounded-full p-0 font-normal aria-selected:opacity-100 hover:bg-black/[0.04]",
              ),
              day_selected:
                "text-white hover:text-white focus:text-white",
              day_today: "bg-black/[0.04] font-semibold",
              day_outside: "text-muted-foreground opacity-40",
              day_disabled: "text-muted-foreground opacity-30",
            }}
            modifiersStyles={{
              selected: {
                backgroundColor: primary,
                color: "#fff",
              },
            }}
          />
          {availableWeekdays.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5 border-t border-black/5 pt-3">
              {availableWeekdays.map((s) => (
                <span
                  key={s.day}
                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                  style={{
                    backgroundColor: `${primary}14`,
                    color: primary,
                  }}
                >
                  {s.day.slice(0, 3)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Times / multi-day summary */}
        <div className="flex min-h-[280px] flex-col rounded-2xl border border-black/8 bg-white p-3">
          <div className="mb-3 flex items-center gap-2 px-1">
            <RiTimeLine className="h-4 w-4" style={{ color: primary }} />
            <p className="text-sm font-semibold text-black/80">
              {isMultiDay ? "Stay details" : "Pick a time"}
            </p>
          </div>

          {!selectedDate ? (
            <div className="flex flex-1 flex-col items-center justify-center rounded-xl bg-black/[0.02] px-4 text-center">
              <RiCalendarLine className="mb-2 h-8 w-8 text-black/20" />
              <p className="text-sm font-medium text-black/45">
                Select a date first
              </p>
              <p className="mt-1 text-xs text-black/35">
                Available times will show up here
              </p>
            </div>
          ) : isMultiDay ? (
            <div className="flex flex-1 flex-col justify-center space-y-3 rounded-xl bg-black/[0.02] p-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-black/40">
                  Check-in
                </p>
                <p className="mt-1 text-base font-semibold text-black">
                  {format(selectedDate, "EEEE, MMM d")}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-black/40">
                  Check-out
                </p>
                <p className="mt-1 text-base font-semibold text-black">
                  {endDate
                    ? format(parseLocalDateKey(endDate), "EEEE, MMM d")
                    : format(
                        addDays(
                          selectedDate,
                          Math.max(1, Math.round(durationMinutes / (24 * 60))),
                        ),
                        "EEEE, MMM d",
                      )}
                </p>
              </div>
              <p className="text-xs text-black/45">
                Duration: {duration || "1 day"}
              </p>
            </div>
          ) : visibleTimeSlots.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center rounded-xl bg-black/[0.02] px-4 text-center">
              <RiTimeLine className="mb-2 h-8 w-8 text-black/20" />
              <p className="text-sm font-medium text-black/45">
                No times left today
              </p>
              <p className="mt-1 text-xs text-black/35">
                Try another date
              </p>
            </div>
          ) : (
            <div className="grid flex-1 grid-cols-2 content-start gap-2 overflow-y-auto sm:grid-cols-3">
              {visibleTimeSlots.map((slot) => {
                const selected = startTime === slot;
                return (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => handleTimeSelect(slot)}
                    className={cn(
                      "h-11 rounded-full border text-sm font-semibold transition-all",
                      selected
                        ? "border-transparent text-white shadow-sm"
                        : "border-black/10 bg-white text-black/70 hover:border-black/20 hover:bg-black/[0.02]",
                    )}
                    style={
                      selected
                        ? { backgroundColor: primary }
                        : undefined
                    }
                  >
                    {formatTimeLabel(slot)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {selectionLabel && (
        <div
          className="flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium"
          style={{ backgroundColor: `${primary}12`, color: primary }}
        >
          <RiCheckLine className="h-4 w-4 shrink-0" />
          <span className="truncate">{selectionLabel}</span>
        </div>
      )}
    </div>
  );
}

export { toLocalDateKey, parseLocalDateKey, parseDurationMinutes };
